import {
  Container,
  Box,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Link,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  useToast
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

export default function Login() {
  const navigate = useNavigate()
  const toast = useToast()
  const { signIn, error: authError, loading, isAuthenticated } = useAuth()
  const [show, setShow] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })


  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      await signIn(formData.email, formData.password)
      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
        status: "success",
        duration: 5000,
        isClosable: true,
      })
      navigate('/dashboard')
    } catch (err) {
      // Error is handled by useAuth hook
      console.error('Login error:', err)
    }
  }

  return (
    <Container maxW="container.sm" py={10}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading variant="journal">Welcome Back</Heading>
          <Text mt={2} color="text.muted">
            Sign in to access your research dashboard
          </Text>
        </Box>

        <Box
          p={8}
          bg="bg.default"
          boxShadow="lg"
          borderRadius="xl"
          color="text.default"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
            'body.textures-enabled &': {
              opacity: 0.1,
              bgImage: 'var(--texture-parchment)',
              bgRepeat: 'repeat',
              bgSize: '200px',
              bgBlendMode: 'multiply',
            }
          }}
        >
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  variant="journal"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    name="password"
                    type={show ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    variant="journal"
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={() => setShow(!show)}>
                      {show ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              {authError && (
                <FormControl isInvalid={true}>
                  <FormErrorMessage>{authError}</FormErrorMessage>
                </FormControl>
              )}

              <Button
                type="submit"
                bg="interactive.primary"
                color="gray.800"
                _hover={{
                  bg: 'interactive.hover'
                }}
                _active={{
                  bg: 'interactive.pressed'
                }}
                size="lg"
                width="full"
                isLoading={loading}
                loadingText="Signing in..."
              >
                Sign In
              </Button>
            </VStack>
          </form>
        </Box>

        <Text textAlign="center">
          Don't have an account?{" "}
          <Link as={RouterLink} to="/signup" variant="journal">
            Register as a Researcher
          </Link>
        </Text>
      </VStack>
    </Container>
  )
}
